import NiceModal, { create, useModal } from '@ebay/nice-modal-react';
import {
  DialogTitle,
  DialogContent,
  DialogContentText,
  Card,
  CardContent,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel as MuiFormControlLabel,
  TextField,
  Box,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { listings } from '@prisma/client';
import { formatCurrency } from '../../utils/formatCurrency';
import { useState } from 'react';
import listingsService from '../../api/services/listings';
import { useAppDispatch } from '../../store/store';
import { fetchListings } from '../../store/slices/listings';

interface AdjustPriceModalProps {
  listing: listings;
}

const createPriceOptions = (listing: listings) => {
  const priceOptions = [];
  // create three price options doing 1.5%, 3%, and 4.5% off the current price
  priceOptions.push(Math.round((listing.price * 0.985) / 100) * 100);
  priceOptions.push(Math.round((listing.price * 0.97) / 100) * 100);
  priceOptions.push(Math.round((listing.price * 0.955) / 100) * 100);
  return priceOptions;
};

const AdjustPriceModal = NiceModal.create(
  ({ listing }: AdjustPriceModalProps) => {
    const modal = useModal();
    const dispatch = useAppDispatch();
    const priceOptions = createPriceOptions(listing);
    const [selectedPrice, setSelectedPrice] = useState<number>(priceOptions[0]);
    const [isCustomPrice, setIsCustomPrice] = useState(false);
    const [customPrice, setCustomPrice] = useState<string>('');
    const [isConfirmed, setIsConfirmed] = useState(false);

    const handleAdjustPrice = async () => {
      const finalPrice = isCustomPrice ? Number(customPrice) : selectedPrice;
      await listingsService.patch({
        id: listing.id,
        data: { price: finalPrice },
        notify: true,
      });
      dispatch(fetchListings({}));
      modal.remove();
    };

    const handlePriceSelection = (value: string) => {
      if (value === 'custom') {
        setIsCustomPrice(true);
      } else {
        setIsCustomPrice(false);
        setSelectedPrice(Number(value));
      }
    };

    return (
      <Dialog open={true} onClose={() => modal.remove()}>
        <DialogTitle>Adjust Price</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={isCustomPrice ? 'custom' : selectedPrice}
            onChange={(e) => handlePriceSelection(e.target.value)}
          >
            {priceOptions.map((price) => {
              const percentageOff = Number(
                (((listing.price - price) / listing.price) * 100).toFixed(1)
              );
              return (
                <FormControlLabel
                  key={price}
                  value={price}
                  control={<Radio />}
                  label={`${formatCurrency(price)} (${percentageOff}% off)`}
                />
              );
            })}
            <FormControlLabel
              value="custom"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Custom Price</span>
                  {isCustomPrice && (
                    <TextField
                      size="small"
                      type="number"
                      value={customPrice}
                      onChange={(e) => setCustomPrice(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      placeholder="Enter price"
                      inputProps={{
                        min: 0,
                        step: 100,
                      }}
                      sx={{ width: 150 }}
                    />
                  )}
                </Box>
              }
            />
          </RadioGroup>
          <MuiFormControlLabel
            control={
              <Checkbox
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
              />
            }
            label="I confirm this price change"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => modal.remove()} color="error">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdjustPrice}
            disabled={
              !isConfirmed ||
              (isCustomPrice && (!customPrice || Number(customPrice) <= 0))
            }
          >
            Update Price
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

export default AdjustPriceModal;
